// Cloud Storage helpers for email attachments that are too big to live inline.
//
// Contract A: mail_attachments/{uid}/{draftKey}/{timestampMs}-{sanitizedFilename}
// in the default bucket. The browser uploads with the Firebase client SDK
// (resumable, with progress); the server reads the object with the Admin SDK
// when the mail is sent, so only this reference travels in drafts and payloads.
// storage.rules already lets any signed-in user read/write every path.

import { ref, uploadBytes, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../../../../firebase';

export const MAIL_ATTACH_STORAGE_ROOT = 'mail_attachments';

/**
 * Stable, path-safe object name: keeps the extension, drops any directory part
 * and odd characters (the real filename travels separately in the attachment's
 * `filename`), collapses dot runs so the result can never contain a '..'
 * segment, and caps the length.
 */
export function sanitizeFilename(name: string, maxLen: number = 120): string {
  let s = String(name || '');
  const lastSep = Math.max(s.lastIndexOf('/'), s.lastIndexOf('\\'));
  if (lastSep >= 0) s = s.slice(lastSep + 1);
  try {
    s = s.normalize('NFKD').replace(/[̀-ͯ]/g, '');
  } catch { /* normalize unsupported: keep as is */ }
  s = s
    .replace(/[^A-Za-z0-9._-]+/g, '_')
    .replace(/\.{2,}/g, '.')
    .replace(/_{2,}/g, '_');
  // Split the extension off BEFORE trimming the base: "文件.pdf" is "_.pdf" by
  // now and trimming the leading "_." first would eat the dot of ".pdf".
  const extMatch = /\.[A-Za-z0-9]{1,10}$/.exec(s);
  const ext = extMatch ? extMatch[0] : '';
  let base = ext ? s.slice(0, s.length - ext.length) : s;
  base = base.replace(/^[._-]+/, '').replace(/[._-]+$/, '');
  const room = Math.max(1, maxLen - ext.length);
  if (base.length > room) base = base.slice(0, room);
  if (!base) base = 'file';
  return `${base}${ext}`;
}

/** A single path segment (uid / draftKey): alphanumerics, '_' and '-' only. */
function safeSegment(v: string, fallback: string): string {
  const s = String(v || '').replace(/[^A-Za-z0-9_-]+/g, '_').slice(0, 80);
  return s || fallback;
}

// Last timestamp handed out by nextUniqueTimestamp (module-wide, so it spans
// every composer and every draftKey on this page).
let lastTimestamp = 0;

/**
 * Date.now(), but never the same value twice in this page session (and never
 * going backwards if the clock is adjusted). sanitizeFilename is lossy
 * ("发票.pdf" and "报价.pdf" are both "file.pdf", "Invoice 1.pdf" and
 * "Invoice_1.pdf" are both "Invoice_1.pdf"), and a multi-file pick starts every
 * upload in the same millisecond - with a bare Date.now() those files would get
 * one and the same object path, the last upload would silently overwrite the
 * other, and a send would deliver one file twice and never the other. A strictly
 * increasing {ts} keeps every path unique without changing the contract-A shape
 * ({ts}-{name}); it can run at most a few ms ahead of the wall clock.
 */
function nextUniqueTimestamp(): number {
  const ts = Math.max(Date.now(), lastTimestamp + 1);
  lastTimestamp = ts;
  return ts;
}

export function buildStoragePath(args: { uid: string; draftKey: string; filename: string; now?: number }): string {
  // An explicit `now` is honoured verbatim (deterministic paths for tests);
  // real uploads always take the collision-free clock.
  const ts = args.now ?? nextUniqueTimestamp();
  return [
    MAIL_ATTACH_STORAGE_ROOT,
    safeSegment(args.uid, 'anon'),
    safeSegment(args.draftKey, 'draft'),
    `${ts}-${sanitizeFilename(args.filename)}`,
  ].join('/');
}

/** Same rule the server enforces before it touches an object. */
export function isMailAttachmentPath(p: unknown): p is string {
  if (typeof p !== 'string' || !p.startsWith(`${MAIL_ATTACH_STORAGE_ROOT}/`)) return false;
  return !p.split('/').some((seg) => seg === '..' || seg === '');
}

/** Short, human reason for a failed upload (raw Firebase errors are cryptic). */
export function describeUploadError(err: unknown): string {
  const code = String((err as any)?.code || '');
  if (code === 'storage/canceled') return 'upload cancelled';
  if (code === 'storage/unauthorized' || code === 'storage/unauthenticated') return 'not signed in - reload and try again';
  if (code === 'storage/quota-exceeded') return 'storage quota exceeded';
  if (code === 'storage/retry-limit-exceeded' || code === 'storage/network-request-failed') return 'network problem - check your connection';
  return String((err as any)?.message || 'upload failed').slice(0, 140);
}

export interface UploadOptions {
  uid: string;
  draftKey: string;
  /** 0-100 */
  onProgress?: (percent: number) => void;
  /** Abort = cancel the in-flight upload (rejects with an AbortError). */
  signal?: AbortSignal;
}

function abortError(): Error {
  const e = new Error('upload cancelled');
  e.name = 'AbortError';
  return e;
}

/** Upload one file (resumable, with progress) and resolve with its object path. */
export function uploadAttachment(file: File, opts: UploadOptions): Promise<{ storagePath: string }> {
  const storagePath = buildStoragePath({ uid: opts.uid, draftKey: opts.draftKey, filename: file.name });
  return new Promise((resolve, reject) => {
    if (opts.signal?.aborted) { reject(abortError()); return; }
    let aborted = false;
    const task = uploadBytesResumable(ref(storage, storagePath), file, {
      contentType: file.type || 'application/octet-stream',
    });
    const onAbort = () => { aborted = true; task.cancel(); };
    opts.signal?.addEventListener('abort', onAbort);
    const cleanup = () => opts.signal?.removeEventListener('abort', onAbort);
    task.on(
      'state_changed',
      (snap) => {
        if (snap.totalBytes > 0) opts.onProgress?.((snap.bytesTransferred / snap.totalBytes) * 100);
      },
      (err) => { cleanup(); reject(aborted ? abortError() : err); },
      () => { cleanup(); resolve({ storagePath }); },
    );
  });
}

/**
 * Best-effort delete. NEVER rejects and ignores anything that is not a
 * mail_attachments/ path - a stale or already-deleted object is not an error
 * (the server deletes used objects after a send and a daily job sweeps orphans).
 */
export async function deleteAttachments(paths: Array<string | null | undefined>): Promise<void> {
  const unique = Array.from(new Set(paths.filter(isMailAttachmentPath)));
  await Promise.all(
    unique.map(async (p) => {
      try {
        await deleteObject(ref(storage, p));
      } catch { /* best-effort */ }
    }),
  );
}

export interface InlineImageUpload {
  uid: string;
  draftKey: string;
  /** Used only to build a readable, sanitised object name. */
  filename: string;
}

/**
 * Upload a pasted picture (already compressed) and return where it now lives.
 *
 * `url` is the object's Firebase download URL: that is what goes in the body as <img src>, so the editor and
 * every preview can show it with a plain <img> (no auth header needed) and the server can find the object again
 * from the URL alone. The object sits under mail_attachments/ like any attachment: it is deleted when the mail
 * is sent, when the draft is discarded, or by the daily orphan sweep.
 */
export async function uploadInlineImage(
  blob: Blob,
  opts: InlineImageUpload,
): Promise<{ storagePath: string; url: string; bytes: number }> {
  const storagePath = buildStoragePath({ uid: opts.uid, draftKey: opts.draftKey, filename: opts.filename });
  const objectRef = ref(storage, storagePath);
  await uploadBytes(objectRef, blob, { contentType: blob.type || 'image/png' });
  const url = await getDownloadURL(objectRef);
  return { storagePath, url, bytes: blob.size };
}
